import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { useAuthStore, type AuthUser } from './auth-store';

export function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);
  const location = useLocation();

  // RFL-106: refresh the user from /auth/me on entry into the protected
  // area so sessions persisted before permissions were plumbed (and any
  // role changes since login) pick up current isSuperAdmin + permission
  // codes. Best-effort — a 401 is handled by the global interceptor.
  //
  // RL-377: the same response now carries the tenant's effective module
  // entitlements, and this is the request that keeps them fresh — a
  // licence change lands on the next entry rather than the next login.
  // `setUser` stores the response whole, so nothing here needs to name
  // the field; what matters is that the refresh replaces the persisted
  // user, dropping a stale list rather than merging into it.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    apiClient
      .get<AuthUser>('/auth/me')
      .then((res) => {
        if (!cancelled && res.data) setUser(res.data);
      })
      .catch(() => {
        /* interceptor handles auth failures; nothing to do here */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, setUser]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
